import React from 'react';
import { DataTable } from '@frontend/app/refresh-components';
import { cn } from '@frontend/shadcn/lib/utils';
import { SocialAccountsTableHeader } from './social-accounts-table-header';
import { SocialAccountsTableControlledProps } from './social-accounts-table-types';

export const SocialAccountsTable: React.FC<SocialAccountsTableControlledProps> = ({
  data,
  columns,
  isLoading = false,
  isRefreshing = false,
  className,
  sorting,
  setSorting,
  searchValue,
  setSearchValue: _setSearchValue,
  currentPage,
  setCurrentPage: _setCurrentPage,
  isExporting,
  setIsExporting: _setIsExporting,
  pageSize,
  filteredData,
  totalPages: _totalPages,
  startRecord,
  endRecord,
  handleSearchChange,
  handlePreviousPage,
  handleNextPage,
  handleExport,
  draggable = false,
  onColumnDragEnd,
  onOpenColumnDialog,
}) => {
  if (isLoading) {
    // Skeleton loader: 7 columns, 5 rows
    return (
      <div
        className={cn('w-full h-full flex flex-col bg-white rounded-lg border border-gray-200', className)}
        role="status"
        aria-busy="true"
        aria-label="Loading social accounts table"
      >
        {/* Skeleton Table Header */}
        <div className="flex border-b border-gray-200 px-6 py-3">
          {[...Array(7)].map((_, idx) => (
            <div
              key={idx}
              className={['h-4 rounded bg-gray-200 animate-pulse flex-1', idx !== 6 ? 'mr-4' : ''].join(' ')}
            />
          ))}
        </div>
        {/* Skeleton Table Rows */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {[...Array(5)].map((_, rowIdx) => (
            <div
              key={rowIdx}
              className={[
                'flex items-center border-b border-gray-100 px-6 py-4 last:border-b-0',
                rowIdx === 4 ? 'last:rounded-b-lg' : '',
              ].join(' ')}
            >
              {[...Array(7)].map((_, colIdx) => (
                <div
                  key={colIdx}
                  className={['h-4 rounded bg-gray-100 animate-pulse flex-1', colIdx !== 6 ? 'mr-4' : ''].join(' ')}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('w-full h-full flex flex-col bg-white rounded-lg border border-gray-200', className)}>
      {/* Table Header */}
      <SocialAccountsTableHeader
        totalCount={filteredData.length}
        currentPage={currentPage}
        pageSize={pageSize}
        startRecord={startRecord}
        endRecord={endRecord}
        onSearchChange={handleSearchChange}
        onPreviousPage={handlePreviousPage}
        onNextPage={handleNextPage}
        searchValue={searchValue}
        onExport={handleExport}
        isExporting={isExporting}
        isRefreshing={isRefreshing}
        onOpenColumnDialog={onOpenColumnDialog}
      />
      {/* Table Content - Extends to fill remaining space */}
      <div className="flex-1 min-h-0">
        <DataTable
          columns={columns}
          data={data}
          sortable
          filterable={false}
          selectable={false}
          bordered
          disableVirtualization
          onSortingChange={setSorting}
          sorting={sorting}
          wrapperClassName="bg-white h-full"
          tableContentClassName="bg-white h-full border-b-0"
          maxHeight="calc(100vh - 116px)"
          draggable={draggable}
          onColumnDragEnd={onColumnDragEnd}
        />
      </div>
    </div>
  );
};
